import { isPlasticNeedingColor, MaterialLike } from '@partsbadger/utils/lib/common/materialColor';

export type MaterialColorModal = 'transparent' | 'colored' | null;

interface MaterialSelection {
    materialId?: string | number | null;
    label?: string;
    /** Key of the coating currently selected in the form, if any. */
    currentCoatingKey?: string | number | null;
    /** Coatings compatible with the material; null when they could not be loaded. */
    compatibleCoatings: Array<{ id: number }> | null;
    materials?: Array<MaterialLike & { id: number }>;
}

/**
 * What choosing a material means for the rest of the part: whether the selected coating no
 * longer fits (or the material was cleared), and whether a plastic needs the color or
 * transparency question first. Without the coatings list the coating is kept, not guessed.
 */
export const getMaterialSelectionOutcome = ({
    materialId,
    label = '',
    currentCoatingKey,
    compatibleCoatings,
    materials,
}: MaterialSelection): { clearCoating: boolean; modal: MaterialColorModal } => {
    let clearCoating = false;
    if (!materialId) {
        clearCoating = true;
    } else if (currentCoatingKey && compatibleCoatings) {
        clearCoating = !compatibleCoatings.some(coating => coating.id === Number(currentCoatingKey));
    }

    const selected = materials?.find(material => material.id == materialId);
    if (label.includes('Clear') && selected?.material_type === 'Plastic') {
        return { clearCoating, modal: 'transparent' };
    }
    if (isPlasticNeedingColor(selected)) {
        return { clearCoating, modal: 'colored' };
    }
    return { clearCoating, modal: null };
};

/**
 * The `material` of a save. An empty field normally means "nothing to send": the key is left
 * out and the server keeps the value. After a process change emptied the field (the material is
 * not made with the new process) it must be sent as null, or the line keeps a material that the
 * card no longer shows and the Review summary still reads it.
 */
export const getMaterialPayload = (
    selectedKey: string | number | null | undefined,
    clearedByProcessChange: boolean
): string | number | null | undefined => {
    if (selectedKey != null && selectedKey !== '') return selectedKey;
    return clearedByProcessChange ? null : undefined;
};

/**
 * Whether the next save must still clear the material. The intent lasts until a material is
 * chosen, not just one save: a save can fail, and sending null again is harmless.
 */
export const materialStillToClear = (
    selectedKey: string | number | null | undefined,
    clearedByProcessChange: boolean
): boolean => clearedByProcessChange && (selectedKey == null || selectedKey === '');
